// School admin: year-end rollover. For each class picked, the server makes next year's
// class (same subject and grades) with its teachers and the students who move on, and
// archives the old one with its results, which stay linked to it. All or nothing: one
// refused class leaves every class as it was.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import ConfirmationModal from '../shared/ConfirmationModal';
import {
  ClassMember,
  RolloverClassPlan,
  RolloverResult,
  SchoolClass,
  fetchClasses,
  fetchRoster,
  rollOverClasses,
} from '../../services/classService';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  linkButtonStyle,
  noticeStyle,
  primaryButtonStyle,
  sectionHeadingStyle,
} from './classesStyles';

const logger = createLogger('RolloverTab');
const CLASS_PAGE_SIZE = 100;
/** The server's limit on classes in one rollover. */
const ROLLOVER_MAX_CLASSES = 200;

interface Plan {
  selected: boolean;
  name: string;
  includeTeachers: boolean;
  /** null: every current student moves on. */
  studentIds: string[] | null;
}

interface Props {
  organizationId?: string | null;
}

/** Every active class in the school, a page of the server's maximum at a time. */
async function loadActiveClasses(organizationId?: string | null): Promise<SchoolClass[]> {
  const classes: SchoolClass[] = [];
  for (let page = 1; ; page += 1) {
    const list = await fetchClasses({ page, pageSize: CLASS_PAGE_SIZE, status: 'active', organizationId });
    classes.push(...list.items);
    if (page >= list.pagination.total_pages) return classes;
  }
}

const RolloverTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState<SchoolClass[] | null>(null);
  const [plans, setPlans] = useState<Record<string, Plan>>({});
  const [students, setStudents] = useState<Record<string, ClassMember[]>>({});
  const [toYear, setToYear] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<RolloverResult[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const loaded = await loadActiveClasses(organizationId);
      setClasses(loaded);
      setPlans(
        Object.fromEntries(
          loaded.map(c => [c.id, { selected: false, name: c.name, includeTeachers: true, studentIds: null }])
        )
      );
    } catch (err) {
      logger.error('Failed to load classes for rollover', err);
      setError(errorMessage(err, t('classes.couldNotLoadClasses')));
    }
  }, [organizationId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (classId: string, change: Partial<Plan>) =>
    setPlans(prev => ({ ...prev, [classId]: { ...prev[classId], ...change } }));

  const chooseStudents = async (classId: string) => {
    try {
      const roster = students[classId] ?? (await fetchRoster(classId)).members.filter(m => m.role === 'student');
      setStudents(prev => ({ ...prev, [classId]: roster }));
      update(classId, { studentIds: roster.map(m => m.user_id) });
    } catch (err) {
      logger.error('Failed to load the roster', err);
      setError(errorMessage(err, t('classes.couldNotLoadClass')));
    }
  };

  const toggleStudent = (classId: string, studentId: string) => {
    const current = plans[classId].studentIds ?? [];
    update(classId, {
      studentIds: current.includes(studentId) ? current.filter(id => id !== studentId) : [...current, studentId],
    });
  };

  const picked = (classes ?? []).filter(c => plans[c.id]?.selected);
  const tooMany = picked.length > ROLLOVER_MAX_CLASSES;
  // Choosing students and then unticking all of them moves the class with nobody in it:
  // allowed (a teacher may want an empty class), but said out loud before it runs.
  const withoutStudents = picked.filter(c => plans[c.id].studentIds?.length === 0).map(c => c.name);
  const confirmMessage = [
    t('studentAdmin.rollover.confirmMessage', { count: picked.length, year: toYear.trim() }),
    withoutStudents.length > 0 ? t('studentAdmin.rollover.noStudentsWarning', { names: withoutStudents.join(', ') }) : '',
  ]
    .filter(Boolean)
    .join(' ');

  const run = async () => {
    setConfirming(false);
    setBusy(true);
    setError(null);
    try {
      const body: RolloverClassPlan[] = picked.map(c => {
        const plan = plans[c.id];
        const name = plan.name.trim();
        return {
          class_id: c.id,
          name: name && name !== c.name ? name : null,
          student_ids: plan.studentIds,
          include_teachers: plan.includeTeachers,
        };
      });
      const response = await rollOverClasses(toYear.trim(), body, organizationId);
      setResults(response.classes);
      await load();
    } catch (err) {
      logger.error('Rollover failed', err);
      setError(errorMessage(err, t('studentAdmin.rollover.failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={cardStyle} aria-labelledby="rollover-heading">
      <h2 id="rollover-heading" style={sectionHeadingStyle}>
        {t('studentAdmin.rollover.title')}
      </h2>
      <p style={{ ...hintStyle, marginTop: 0 }}>{t('studentAdmin.rollover.hint')}</p>

      {error && <div style={{ ...errorStyle, marginTop: '1rem' }} role="alert">{error}</div>}
      {results && (
        <div style={{ ...noticeStyle, marginTop: '1rem' }} role="status">
          {t('studentAdmin.rollover.done', { count: results.length })}
          <ul style={{ margin: '6px 0 0', paddingLeft: '1.2rem' }}>
            {results.map(r => (
              <li key={r.new_class_id}>
                <Link to={`/classes/${r.new_class_id}`}>{r.name}</Link>{' '}
                {t('studentAdmin.rollover.resultCounts', { teachers: r.teachers, students: r.students })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ margin: '1rem 0' }}>
        <label htmlFor="rollover-year" style={labelStyle}>
          {t('studentAdmin.rollover.newSchoolYear')} *
        </label>
        <input
          id="rollover-year"
          style={{ ...inputStyle, width: 180 }}
          maxLength={20}
          placeholder={t('classes.schoolYearPlaceholder')}
          value={toYear}
          onChange={e => setToYear(e.target.value)}
        />
      </div>

      {!classes ? (
        !error && <LoadingIndicator message={t('classes.loading')} />
      ) : classes.length === 0 ? (
        <p style={hintStyle}>{t('studentAdmin.rollover.noClasses')}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {classes.map(c => {
            const plan = plans[c.id];
            if (!plan) return null;
            const roster = students[c.id];
            return (
              <li key={c.id} data-testid="rollover-class" style={{ borderTop: '1px solid #e2e8f0', padding: '0.75rem 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={plan.selected}
                    disabled={busy}
                    onChange={e => update(c.id, { selected: e.target.checked })}
                  />
                  {c.name}
                  <span style={{ fontWeight: 400, color: '#64748b' }}>
                    {[c.school_year, t('studentAdmin.rollover.memberCounts', { teachers: c.teacher_count, students: c.student_count })]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </label>
                {plan.selected && (
                  <div style={{ marginLeft: 24, marginTop: 8 }}>
                    <label htmlFor={`rollover-name-${c.id}`} style={labelStyle}>
                      {t('studentAdmin.rollover.newName')}
                    </label>
                    <input
                      id={`rollover-name-${c.id}`}
                      style={{ ...inputStyle, width: 260 }}
                      maxLength={255}
                      value={plan.name}
                      disabled={busy}
                      onChange={e => update(c.id, { name: e.target.value })}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={plan.includeTeachers}
                        disabled={busy}
                        onChange={e => update(c.id, { includeTeachers: e.target.checked })}
                      />
                      {t('studentAdmin.rollover.includeTeachers')}
                    </label>
                    <div style={{ fontSize: '0.85rem', marginTop: 8 }}>
                      {plan.studentIds === null ? (
                        <>
                          {t('studentAdmin.rollover.allStudentsMove')}{' '}
                          <button type="button" style={linkButtonStyle} disabled={busy} onClick={() => chooseStudents(c.id)}>
                            {t('studentAdmin.rollover.chooseStudents')}
                          </button>
                        </>
                      ) : (
                        <>
                          <span>
                            {t('studentAdmin.rollover.studentsChosen', {
                              count: plan.studentIds.length,
                              total: roster?.length ?? 0,
                            })}
                          </span>{' '}
                          <button
                            type="button"
                            style={linkButtonStyle}
                            disabled={busy}
                            onClick={() => update(c.id, { studentIds: null })}
                          >
                            {t('studentAdmin.rollover.moveAllStudents')}
                          </button>
                          <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
                            {(roster ?? []).map(m => (
                              <li key={m.user_id}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input
                                    type="checkbox"
                                    checked={plan.studentIds?.includes(m.user_id) ?? false}
                                    disabled={busy}
                                    onChange={() => toggleStudent(c.id, m.user_id)}
                                  />
                                  {m.display_name || m.email || m.user_id}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        style={{ ...primaryButtonStyle, marginTop: '1rem' }}
        disabled={busy || picked.length === 0 || tooMany || !toYear.trim()}
        onClick={() => setConfirming(true)}
      >
        {t('studentAdmin.rollover.run', { count: picked.length })}
      </button>
      {tooMany && (
        <p style={{ ...hintStyle, color: '#991b1b' }} role="alert">
          {t('studentAdmin.rollover.tooMany', { max: ROLLOVER_MAX_CLASSES })}
        </p>
      )}

      <ConfirmationModal
        isOpen={confirming}
        title={t('studentAdmin.rollover.confirmTitle')}
        message={confirmMessage}
        confirmText={t('studentAdmin.rollover.confirm')}
        loading={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={run}
      />
    </section>
  );
};

export default RolloverTab;
